import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  CircularProgress, 
  Alert, 
  Link, 
  Container,
  Divider,
  InputAdornment,
  IconButton,
  useTheme,
  alpha
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person as PersonIcon,
  Email as EmailIcon,
  VpnKey as VpnKeyIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';

function Login() {
  const theme = useTheme();
  
  // State for login form
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // State for signup form
  const [signupUsername, setSignupUsername] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [email, setEmail] = useState('');
  const [signupLoading, setSignupLoading] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  
  // State for confirmation form
  const [confirmUsername, setConfirmUsername] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  
  // State for password reset form
  const [forgotPasswordUsername, setForgotPasswordUsername] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // State for first-time login challenge
  const [isFirstTimeLogin, setIsFirstTimeLogin] = useState(false);
  const [challengeSession, setChallengeSession] = useState(null);
  const [tempPassword, setTempPassword] = useState('');
  const [firstTimeLoginLoading, setFirstTimeLoginLoading] = useState(false);
  
  // State for form mode and error messages
  const [mode, setMode] = useState('login'); // 'login', 'signup', 'confirm', 'forgot-password', 'reset-password', 'first-time-login'
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  const { signIn, signUp, confirmSignUp, resendConfirmationCode, forgotPassword, confirmForgotPassword, respondToNewPasswordChallenge } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const result = await signIn(username, password);
      
      // Check if this is a first-time login challenge
      if (result?.challengeName === 'NEW_PASSWORD_REQUIRED') {
        console.log('First-time login detected - switching to password change form');
        setIsFirstTimeLogin(true);
        setChallengeSession(result.session);
        setTempPassword(password); // The password they entered was the temp password
        setMode('first-time-login');
        setSuccessMessage('Welcome! Please set your permanent password to continue.');
      } else {
        // Regular successful login
        navigate('/');
      }
    } catch (err) {
      if (err.message && err.message.includes('not confirmed')) {
        // If user is not confirmed, switch to confirmation mode
        setError('Your account is not confirmed. Please check your email for a verification code.');
        setMode('confirm');
        setConfirmUsername(username);
      } else {
        setError(err.message || 'Failed to sign in');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setSignupLoading(true);
    
    try {
      await signUp(signupUsername, signupPassword, email);
      setSuccessMessage('Registration successful! Please check your email for a verification code.');
      setMode('confirm');
      setConfirmUsername(signupUsername);
    } catch (err) {
      setError(err.message || 'Failed to sign up');
    } finally {
      setSignupLoading(false);
    }
  };

  const handleConfirmSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setConfirmLoading(true);
    
    try {
      await confirmSignUp(confirmUsername, confirmationCode);
      setSuccessMessage('Email verified successfully! You can now log in.');
      setMode('login');
      setUsername(confirmUsername);
    } catch (err) {
      setError(err.message || 'Failed to confirm registration');
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setSuccessMessage('');
    setResendLoading(true);
    
    try {
      await resendConfirmationCode(confirmUsername);
      setSuccessMessage('A new verification code has been sent to your email.');
    } catch (err) {
      setError(err.message || 'Failed to resend verification code');
    } finally {
      setResendLoading(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowSignupPassword = () => {
    setShowSignupPassword(!showSignupPassword);
  };

  const handleClickShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setForgotPasswordLoading(true);
    
    try {
      await forgotPassword(forgotPasswordUsername);
      setSuccessMessage('Password reset code sent! Please check your email.');
      setMode('reset-password');
    } catch (err) {
      setError(err.message || 'Failed to send password reset code');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const validatePassword = (password) => {
    const requirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    
    return {
      isValid: Object.values(requirements).every(req => req),
      requirements
    };
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setResetPasswordLoading(true);
    
    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setResetPasswordLoading(false);
      return;
    }

    // Validate password strength
    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      setError('Password must be at least 8 characters long and include uppercase, lowercase, numbers, and special characters.');
      setResetPasswordLoading(false);
      return;
    }
    
    try {
      await confirmForgotPassword(forgotPasswordUsername, resetCode, newPassword);
      setSuccessMessage('Password reset successful! You can now log in with your new password.');
      setMode('login');
      setUsername(forgotPasswordUsername);
      // Clear password reset form
      setForgotPasswordUsername('');
      setResetCode('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const handleFirstTimeLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setFirstTimeLoginLoading(true);
    
    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setFirstTimeLoginLoading(false);
      return;
    }

    // Validate password strength
    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      setError('Password must be at least 8 characters long and include uppercase, lowercase, numbers, and special characters.');
      setFirstTimeLoginLoading(false);
      return;
    }
    
    try {
      await respondToNewPasswordChallenge(username, tempPassword, newPassword, challengeSession);
      setSuccessMessage('Password set successfully! Welcome to Healin Healthcare Provider Portal.');
      // Small delay to show success message, then navigate
      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to set new password');
    } finally {
      setFirstTimeLoginLoading(false);
    }
  };

  const renderLoginForm = () => (
    <Box component="form" onSubmit={handleLogin} sx={{ width: '100%' }}>
      <TextField
        fullWidth
        margin="normal"
          id="username"
        label="Username"
        variant="outlined"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PersonIcon color="primary" />
            </InputAdornment>
          ),
        }}
      />
      
      <TextField
        fullWidth
        margin="normal"
          id="password"
        label="Password"
        type={showPassword ? 'text' : 'password'}
        variant="outlined"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <VpnKeyIcon color="primary" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      
      <Button
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        size="large"
        disabled={loading}
        sx={{ mt: 3, mb: 2, py: 1.2 }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
            Signing in...
          </Box>
        ) : (
          'Sign In'
        )}
      </Button>
      
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
        Don't have an account?{' '}
          <Link
            component="button"
          type="button" 
            variant="body2"
            color="primary"
          onClick={() => setMode('signup')}
            sx={{ fontWeight: 500 }}
        >
          Sign Up
          </Link>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          <Link
            component="button"
            type="button"
            variant="body2"
            color="primary"
            onClick={() => setMode('forgot-password')}
            sx={{ fontWeight: 500 }}
          >
            Forgot your password?
          </Link>
        </Typography>
      </Box>
    </Box>
  );

  const renderSignUpForm = () => (
    <Box component="form" onSubmit={handleSignUp} sx={{ width: '100%' }}>
      <TextField
        fullWidth
        margin="normal"
          id="signupUsername"
        label="Username"
        variant="outlined"
          value={signupUsername}
          onChange={(e) => setSignupUsername(e.target.value)}
          required
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PersonIcon color="primary" />
            </InputAdornment>
          ),
        }}
      />
      
      <TextField
        fullWidth
        margin="normal"
        id="email"
        label="Email"
          type="email"
        variant="outlined"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <EmailIcon color="primary" />
            </InputAdornment>
          ),
        }}
      />
      
      <TextField
        fullWidth
        margin="normal"
          id="signupPassword"
        label="Password"
        type={showSignupPassword ? 'text' : 'password'}
        variant="outlined"
          value={signupPassword}
          onChange={(e) => setSignupPassword(e.target.value)}
          required
        helperText="Password must be at least 8 characters long and include uppercase, lowercase, numbers, and special characters."
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <VpnKeyIcon color="primary" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowSignupPassword}
                edge="end"
              >
                {showSignupPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      
      <Button
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        size="large"
        disabled={signupLoading}
        sx={{ mt: 3, mb: 2, py: 1.2 }}
      >
        {signupLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
            Signing up...
          </Box>
        ) : (
          'Sign Up'
        )}
      </Button>
      
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
        Already have an account?{' '}
          <Link
            component="button"
          type="button" 
            variant="body2"
            color="primary"
          onClick={() => setMode('login')}
            sx={{ fontWeight: 500 }}
        >
          Sign In
          </Link>
        </Typography>
      </Box>
    </Box>
  );

  const renderConfirmationForm = () => (
    <Box component="form" onSubmit={handleConfirmSignUp} sx={{ width: '100%' }}>
      <TextField
        fullWidth
        margin="normal"
          id="confirmUsername"
        label="Username"
        variant="outlined"
          value={confirmUsername}
          onChange={(e) => setConfirmUsername(e.target.value)}
          required
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PersonIcon color="primary" />
            </InputAdornment>
          ),
        }}
      />
      
      <TextField
        fullWidth
        margin="normal"
          id="confirmationCode"
        label="Verification Code"
        variant="outlined"
          value={confirmationCode}
          onChange={(e) => setConfirmationCode(e.target.value)}
          required
        helperText="Please enter the verification code sent to your email."
      />
      
      <Button
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        size="large"
        disabled={confirmLoading}
        sx={{ mt: 3, mb: 2, py: 1.2 }}
      >
        {confirmLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
            Verifying...
          </Box>
        ) : (
          'Verify Email'
        )}
      </Button>
      
      <Box sx={{ mt: 3, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Link
          component="button"
          type="button"
          variant="body2"
          color="primary"
          onClick={handleResendCode}
          disabled={resendLoading}
          sx={{ fontWeight: 500 }}
        >
          {resendLoading ? 'Sending...' : 'Resend verification code'}
        </Link>
        
        <Link
          component="button"
          type="button" 
          variant="body2"
          color="primary"
          onClick={() => setMode('login')}
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            fontWeight: 500 
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 16, mr: 0.5 }} />
          Back to Sign In
        </Link>
      </Box>
    </Box>
  );

  const renderForgotPasswordForm = () => (
    <Box component="form" onSubmit={handleForgotPassword} sx={{ width: '100%' }}>
      <TextField
        fullWidth
        margin="normal"
        id="forgotPasswordUsername"
        label="Username or Email"
        variant="outlined"
        value={forgotPasswordUsername}
        onChange={(e) => setForgotPasswordUsername(e.target.value)}
        required
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PersonIcon color="primary" />
            </InputAdornment>
          ),
        }}
      />
      
      <Button
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        size="large"
        disabled={forgotPasswordLoading}
        sx={{ mt: 3, mb: 2, py: 1.2 }}
      >
        {forgotPasswordLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
            Sending Reset Code...
          </Box>
        ) : (
          'Send Reset Code'
        )}
      </Button>
      
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Link
          component="button"
          type="button" 
          variant="body2"
          color="primary"
          onClick={() => setMode('login')}
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            fontWeight: 500 
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 16, mr: 0.5 }} />
          Back to Sign In
        </Link>
      </Box>
    </Box>
  );

  const renderResetPasswordForm = () => (
    <Box component="form" onSubmit={handleResetPassword} sx={{ width: '100%' }}>
      <TextField
        fullWidth
        margin="normal"
        id="resetUsername"
        label="Username"
        variant="outlined"
        value={forgotPasswordUsername}
        disabled
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PersonIcon color="primary" />
            </InputAdornment>
          ),
        }}
      />
      
      <TextField
        fullWidth
        margin="normal"
        id="resetCode"
        label="Reset Code"
        variant="outlined"
        value={resetCode}
        onChange={(e) => setResetCode(e.target.value)}
        required
        helperText="Enter the code sent to your email"
      />
      
      <TextField
        fullWidth
        margin="normal"
        id="newPassword"
        label="New Password"
        type={showNewPassword ? 'text' : 'password'}
        variant="outlined"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        required
        helperText="Password must be at least 8 characters long and include uppercase, lowercase, numbers, and special characters."
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <VpnKeyIcon color="primary" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowNewPassword}
                edge="end"
              >
                {showNewPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      
      <TextField
        fullWidth
        margin="normal"
        id="confirmPassword"
        label="Confirm New Password"
        type={showConfirmPassword ? 'text' : 'password'}
        variant="outlined"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        required
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <VpnKeyIcon color="primary" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowConfirmPassword}
                edge="end"
              >
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      
      <Button
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        size="large"
        disabled={resetPasswordLoading}
        sx={{ mt: 3, mb: 2, py: 1.2 }}
      >
        {resetPasswordLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
            Resetting Password...
          </Box>
        ) : (
          'Reset Password'
        )}
      </Button>
      
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Link
          component="button"
          type="button" 
          variant="body2"
          color="primary"
          onClick={() => setMode('forgot-password')}
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            fontWeight: 500 
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 16, mr: 0.5 }} />
          Back to Reset Request
        </Link>
      </Box>
    </Box>
  );

  const renderFirstTimeLoginForm = () => (
    <Box component="form" onSubmit={handleFirstTimeLogin} sx={{ width: '100%' }}>
      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
        <strong>Welcome to Healin Healthcare Provider Portal!</strong><br />
        This is your first login. Please set a permanent password to secure your account.
      </Alert>
      
      <TextField
        fullWidth
        margin="normal"
        id="firstTimeUsername"
        label="Username/Email"
        variant="outlined"
        value={username}
        disabled
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PersonIcon color="primary" />
            </InputAdornment>
          ),
        }}
      />
      
      <TextField
        fullWidth
        margin="normal"
        id="tempPasswordDisplay"
        label="Temporary Password"
        variant="outlined"
        value="••••••••"
        disabled
        helperText="The temporary password you used to log in"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <VpnKeyIcon color="primary" />
            </InputAdornment>
          ),
        }}
      />
      
      <TextField
        fullWidth
        margin="normal"
        id="newPasswordFirstTime"
        label="New Password"
        type={showNewPassword ? 'text' : 'password'}
        variant="outlined"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        required
        helperText="Password must be at least 8 characters long and include uppercase, lowercase, numbers, and special characters."
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <VpnKeyIcon color="primary" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowNewPassword}
                edge="end"
              >
                {showNewPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      
      <TextField
        fullWidth
        margin="normal"
        id="confirmPasswordFirstTime"
        label="Confirm New Password"
        type={showConfirmPassword ? 'text' : 'password'}
        variant="outlined"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        required
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <VpnKeyIcon color="primary" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowConfirmPassword}
                edge="end"
              >
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      
      <Button
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        size="large"
        disabled={firstTimeLoginLoading}
        sx={{ mt: 3, mb: 2, py: 1.2 }}
      >
        {firstTimeLoginLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
            Setting Password...
          </Box>
        ) : (
          'Set Password & Continue'
        )}
      </Button>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        py: 8
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={2}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            p: { xs: 3, sm: 5 },
            borderRadius: 2,
            maxWidth: 500,
            mx: 'auto'
          }}
        >
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
              <img 
                src="/images/healin-logo.svg" 
                alt="Healin Logo" 
                style={{ 
                  height: '60px', 
                  width: 'auto',
                  maxWidth: '250px',
                  objectFit: 'contain'
                }} 
              />
            </Box>
            <Typography variant="subtitle1" color="text.secondary">
              Healthcare Provider Portal
            </Typography>
          </Box>
          
          <Divider sx={{ width: '100%', mb: 4 }} />
          
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3, 
                width: '100%',
                borderRadius: 2
              }}
            >
              {error}
            </Alert>
          )}
          
          {successMessage && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 3, 
                width: '100%',
                borderRadius: 2
              }}
            >
              {successMessage}
            </Alert>
          )}
        
        {mode === 'login' && renderLoginForm()}
        {mode === 'signup' && renderSignUpForm()}
        {mode === 'confirm' && renderConfirmationForm()}
        {mode === 'forgot-password' && renderForgotPasswordForm()}
        {mode === 'reset-password' && renderResetPasswordForm()}
        {mode === 'first-time-login' && renderFirstTimeLoginForm()}
        </Paper>
      </Container>
    </Box>
  );
}

export default Login;